import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withModifiers as _withModifiers, renderSlot as _renderSlot, vShow as _vShow, mergeProps as _mergeProps, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "k-file-uploader__file-container relative-position"
}
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "k-file-uploader__video-cover rounded-12" }
const _hoisted_4 = ["src"]
const _hoisted_5 = {
  key: 0,
  class: "flex gap-lg"
}
const _hoisted_6 = ["onDragstart", "onDrop"]
const _hoisted_7 = { class: "k-file-uploader__slide-footer shadow-1 rounded-16 flex items-center justify-between no-wrap full-width q-px-sm q-py-xs" }
const _hoisted_8 = { key: 1 }
const _hoisted_9 = {
  key: 1,
  class: "fs-13px fw-400 text-cool-gray-60 q-mb-12"
}
const _hoisted_10 = { class: "fs-13px fw-400 text-error-100 q-mb-12 text-center" }
const _hoisted_11 = {
  key: 3,
  class: "q-pa-sm bg-info-50 flex flex-center gap-xs rounded-12"
}
const _hoisted_12 = { class: "fw-400 fs-13px text-info-100" }

import { ref, computed, watchEffect, watch } from 'vue'
import placeholderImage from 'src/shared/static/images/uploader-placeholder.png'

interface ITextObject {
  image: {
    replace: string
    single_upload: string
    multiple_upload: string
    hint: string
    error: string
    alert: string
  }
  video: {
    replace: string
    single_upload: string
    hint: string
    error: string
  }
}


export default /*@__PURE__*/_defineComponent({
  __name: 'KFileUploader',
  props: /*@__PURE__*/_mergeModels({
    id: {},
    type: { default: 'image' },
    variation: { default: 'default' },
    maxItem: { default: 3 },
    maxImageSize: { default: 0.8 },
    maxVideoSize: { default: 10 },
    text: { default: () =>
      ({
        image: {
          replace: 'Replace image',
          single_upload: 'Upload image',
          multiple_upload: 'Upload multiple images',
          hint: 'Choose an image to upload',
          error: 'Image upload failed',
          alert: 'Max number of images reached',
        },
        video: {
          replace: 'Replace video',
          single_upload: 'Upload video',
          hint: 'Choose a video to upload',
          error: 'Video upload failed',
        },
      }) as ITextObject }
  }, {
    "modelValue": { required: true },
    "modelModifiers": {},
    "sort": { required: true },
    "sortModifiers": {},
    "newFile": { default: () => [] },
    "newFileModifiers": {},
  }),
  emits: ["update:modelValue", "update:sort", "update:newFile"],
  setup(__props: any) {

const modelValue = _useModel<(string | File)[]>(__props, "modelValue")
const sort = _useModel<number[]>(__props, 'sort')
const newFile = _useModel<File[]>(__props, 'newFile')

const props = __props

const fileRef = ref<File | null>(null)
const fileUrlRef = ref<(string | File)[]>(modelValue.value)
const filePreviewIndexRef = ref<number>(0)
const sortRef = ref<number[]>(sort.value)
const newFileRef = ref<File[]>(newFile.value)
const errorRef = ref<boolean>(false)
const videoRef = ref<HTMLVideoElement | null>(null)
const draggedIndexRef = ref<number | null>(null)
const clickedSlideIndex = ref<number | null>(null)

const maxFileSizeByte = computed(() => {
  if (props.type === 'video') return props.maxVideoSize * 1000000
  return props.maxImageSize * 1000000
})

const isFullVariation = computed(() => props.variation === 'full')

const btnIcon = computed(() => {
  if (fileUrlRef.value.length > 0) return 'refresh'
  if (props.type === 'video') return 'video'
  return 'camera'
})

const btnLabel = computed(() => {
  if (props.type === 'image') {
    if (fileUrlRef.value.length > 0) return props.text.image.replace
    return props.text.image.single_upload
  } else if (props.type === 'video') {
    if (fileUrlRef.value.length > 0) return props.text.video.replace
    return props.text.video.single_upload
  }
  return props.text.image.multiple_upload
})

const hint = computed(() => {
  if (props.type === 'video') return props.text.video.hint
  return props.text.image.hint
})

const errorText = computed(() => {
  if (props.type === 'video') return props.text.video.error
  return props.text.image.error
})

const preview = computed(() => {
  return fileUrlRef.value.map((item) => (typeof item !== 'string' ? URL.createObjectURL(item) : item))
})

const isSlide = computed(() => props.type === 'slide')

function onUploaderClick(_event: Event, index: number | null = null) {
  clickedSlideIndex.value = index
  const element = document.querySelector(`label[for="${props.id}"]`)
  if ((element && modelValue.value.length < props.maxItem) || index !== null) {
    ;(element as HTMLElement).click()
  }
}

function handleUpload() {
  errorRef.value = false
  if (fileRef.value) {
    if (props.type === 'slide') {
      const largestSortNumber = sort.value.length ? Math.max(...sortRef.value) : 0
      const localSort = largestSortNumber + 1

      if (modelValue.value.length < props.maxItem) {
        sortRef.value.push(localSort)
        fileUrlRef.value.push(fileRef.value)
        filePreviewIndexRef.value = fileUrlRef.value.length - 1
      } else if (clickedSlideIndex.value !== null) {
        sortRef.value[clickedSlideIndex.value] = localSort
        fileUrlRef.value[clickedSlideIndex.value] = fileRef.value
        filePreviewIndexRef.value = clickedSlideIndex.value
      }

      modelValue.value = fileUrlRef.value
      sort.value = sortRef.value
    } else {
      sortRef.value = [1]
      fileUrlRef.value = [fileRef.value]
      modelValue.value = fileUrlRef.value
      sort.value = sortRef.value
    }
    fileRef.value = null
    GenerateNewFile()
  }
}

function GenerateNewFile() {
  newFileRef.value = []
  const files: File[] = []

  const localNewFiles = fileUrlRef.value

  for (let i = 0; i < localNewFiles.length; i++) {
    if (typeof localNewFiles[i] !== 'string') {
      files.push(localNewFiles[i] as File)
    }
  }

  newFileRef.value = [...files]
  newFile.value = newFileRef.value
}

function handleDeleteFile(selectedIndex: number) {
  if (!isSlide.value) {
    fileUrlRef.value.splice(0)
    modelValue.value = fileUrlRef.value
    GenerateNewFile()
    newFileRef.value.splice(0)
    sortRef.value = []
    sort.value = sortRef.value
    newFile.value = newFileRef.value
  } else {
    const index = selectedIndex
    filePreviewIndexRef.value = 0
    fileUrlRef.value.splice(index, 1)
    modelValue.value = fileUrlRef.value
    sortRef.value.splice(index, 1)
    sort.value = sortRef.value
    GenerateNewFile()
  }
}

function handleDropEvent<T>(arr: T[], index: number): T[] {
  const newArray = [...arr]
  const dragged = newArray[draggedIndexRef.value as number]
  newArray.splice(draggedIndexRef.value as number, 1)
  newArray.splice(index, 0, dragged)
  return newArray
}

function drop(index: number) {
  if (draggedIndexRef.value === null) return
  fileUrlRef.value = handleDropEvent(fileUrlRef.value, index)
  sortRef.value = handleDropEvent(sortRef.value, index)
  modelValue.value = fileUrlRef.value
  draggedIndexRef.value = null
  sort.value = sortRef.value
}

function onRejected() {
  errorRef.value = true
}

watchEffect(() => {
  fileUrlRef.value = modelValue.value
})

watch(
  () => props.type,
  () => {
    modelValue.value = []
    fileUrlRef.value = []
    sortRef.value = []
    sort.value = sortRef.value
    errorRef.value = false
  },
)

watch(sort, () => {
  sortRef.value = sort.value
})

return (_ctx: any,_cache: any) => {
  const _component_q_img = _resolveComponent("q-img")!
  const _component_KIcon = _resolveComponent("KIcon")!
  const _component_KActionBtn = _resolveComponent("KActionBtn")!
  const _component_kIcon = _resolveComponent("kIcon")!
  const _component_q_file = _resolveComponent("q-file")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["k-file-uploader column items-center", { 'k-file-uploader__placeholder': !fileUrlRef.value.length, 'full-width': isFullVariation.value }])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass({ 'full-width': isFullVariation.value })
    }, [
      (__props.type !== 'slide')
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            (__props.type === 'image')
              ? (_openBlock(), _createBlock(_component_q_img, {
                  key: 0,
                  src: preview.value[0],
                  "object-fit": "cover",
                  class: _normalizeClass(["k-file-uploader__image non-selectable rounded-12 relative-position", [
            { 'full-width': isFullVariation.value },
            !fileUrlRef.value.length ? 'k-file-uploader__placeholder-border' : 'k-file-uploader__image-border',
          ]]),
                  "placeholder-src": _unref(placeholderImage),
                  onClick: onUploaderClick
                }, null, 8, ["src", "class", "placeholder-src"]))
              : (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: _normalizeClass({ 'full-width': isFullVariation.value })
                }, [
                  (!preview.value.length)
                    ? (_openBlock(), _createBlock(_component_q_img, {
                        key: 0,
                        src: _unref(placeholderImage),
                        "object-fit": "cover",
                        class: _normalizeClass(["k-file-uploader__image non-selectable rounded-12 relative-position", [
              { 'full-width': isFullVariation.value },
              !fileUrlRef.value.length ? 'k-file-uploader__placeholder-border' : 'k-file-uploader__image-border',
            ]]),
                        "placeholder-src": _unref(placeholderImage),
                        onClick: onUploaderClick
                      }, null, 8, ["src", "class", "placeholder-src"]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                          (_openBlock(), _createElementBlock("video", {
                            ref_key: "videoRef",
                            ref: videoRef,
                            class: "k-file-uploader__video rounded-12",
                            key: preview.value[0]
                          }, [
                            _createElementVNode("source", {
                              src: preview.value[0],
                              type: "video/mp4"
                            }, null, 8, _hoisted_4),
                            _cache[3] || (_cache[3] = _createTextVNode(" Your browser does not support the video tag. ", -1))
                          ]))
                        ])
                      ]))
                ], 2)),
            _createElementVNode("div", {
              class: _normalizeClass(["k-file-uploader__slide-footer-container absolute q-px-xs", { 'k-file-uploader__active-slide-footer': preview.value.length }])
            }, [
              _createElementVNode("div", {
                class: "k-file-uploader__slide-footer shadow-1 rounded-16 flex items-center justify-between no-wrap rounded-circle q-pa-xs cursor-pointer",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (!isSlide.value && handleDeleteFile(0)))
              }, [
                _createVNode(_component_KIcon, {
                  name: "trash",
                  class: "cursor-pointer"
                })
              ])
            ], 2)
          ]))
        : _createCommentVNode("", true)
    ], 2),
    (isSlide.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.maxItem, (n, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: index,
              class: "k-file-uploader__file-container relative-position flex gap-lg",
              draggable: "true",
              onDragstart: ($event: any) => (draggedIndexRef.value = index),
              onDragover: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
              onDrop: ($event: any) => (drop(index))
            }, [
              _createVNode(_component_q_img, {
                src: preview.value[index],
                "object-fit": "cover",
                class: _normalizeClass(["k-file-uploader__image non-selectable rounded-12 relative-position", preview.value[index] ? 'k-file-uploader__image-border' : 'k-file-uploader__placeholder-border']),
                "placeholder-src": _unref(placeholderImage),
                onClick: ($event: any) => (onUploaderClick($event, index))
              }, null, 8, ["src", "class", "placeholder-src", "onClick"]),
              _createElementVNode("div", {
                class: _normalizeClass(["k-file-uploader__slide-footer-container absolute q-px-xs full-width", { 'k-file-uploader__active-slide-footer': preview.value[index] }])
              }, [
                _createElementVNode("div", _hoisted_7, [
                  (isSlide.value)
                    ? (_openBlock(), _createBlock(_component_KIcon, {
                        key: 0,
                        name: "arrangement",
                        color: preview.value.length === 1 ? 'cool-gray-50 disabled' : 'cool-gray-70',
                        class: "cursor-pointer"
                      }, null, 8, ["color"]))
                    : _createCommentVNode("", true),
                  (isSlide.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_8, "slide-" + _toDisplayString(n), 1))
                    : _createCommentVNode("", true),
                  _createVNode(_component_KIcon, {
                    name: "trash",
                    class: "cursor-pointer",
                    onClick: ($event: any) => (handleDeleteFile(index))
                  }, null, 8, ["onClick"])
                ])
              ], 2)
            ], 40, _hoisted_6))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_KActionBtn, {
      label: btnLabel.value,
      size: "md",
      "left-icon": btnIcon.value,
      "right-icon": "arrow-right",
      class: "q-mb-sm q-mt-12",
      disable: preview.value.length === __props.maxItem,
      onClick: onUploaderClick
    }, null, 8, ["label", "left-icon", "disable"]),
    (!errorRef.value)
      ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(hint.value), 1))
      : _renderSlot(_ctx.$slots, "error", { key: 2 }, () => [
          _createElementVNode("span", _hoisted_10, _toDisplayString(errorText.value), 1)
        ]),
    (preview.value.length === __props.maxItem)
      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
          _createVNode(_component_kIcon, { name: "info" }),
          _createElementVNode("span", _hoisted_12, _toDisplayString(__props.text.image.alert), 1)
        ]))
      : _createCommentVNode("", true),
    _withDirectives(_createVNode(_component_q_file, _mergeProps({
      modelValue: fileRef.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((fileRef).value = $event))
    }, _ctx.$attrs, {
      borderless: "",
      outline: "",
      dense: "",
      "hide-bottom-space": "",
      "bottom-slots": "",
      for: __props.id,
      "max-file-size": maxFileSizeByte.value,
      "onUpdate:modelValue": handleUpload,
      onRejected: onRejected
    }), null, 16, ["modelValue", "for", "max-file-size"]), [
      [_vShow, false]
    ])
  ], 2))
}
}

})